import { useState, useRef } from 'react';
import TirarFoto from '../TirarFoto/TirarFoto';
import ModalVisualizarImagem from './ModalVisualizarImagem';
import { GerenciadorImagensProps } from './types';
import ModalDecisao from '../Modais/ModalDecisao';

export default function GerenciadorImagens({
  imagens,
  maxImagens = 5,
  onAdicionarImagem,
  onRemoverImagem,
  loading = false,
}: GerenciadorImagensProps) {
  const [mostrarCamera, setMostrarCamera] = useState(false);
  const [imagemSelecionada, setImagemSelecionada] = useState<{
    url: string;
    nome: string;
    tamanho: string;
    id: string;
  } | null>(null);
  const [imagemParaExcluir, setImagemParaExcluir] = useState<string | null>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && imagens.length < maxImagens) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target?.result as string;
        onAdicionarImagem({
          url,
          nome: file.name,
          tipo: 'upload',
        });
      };
      reader.readAsDataURL(file);
    }
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
    }
  };

  const handleCapturePhoto = (imagemUrl: string) => {
    if (imagens.length < maxImagens) {
      onAdicionarImagem({
        url: imagemUrl,
        nome: `foto_${Date.now()}.jpg`,
        tipo: 'camera',
      });
    }
  };

  const handleRemoverImagem = (imagemId: string) => {
    // Timeout para garantir que o evento seja processado no iPad
    setTimeout(() => {
      setImagemParaExcluir(imagemId);
    }, 100);
  };

  const confirmarExclusao = () => {
    if (imagemParaExcluir) {
      onRemoverImagem(imagemParaExcluir);
      setImagemParaExcluir(null);
    }
  };

  const formatarTamanhoArquivo = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleVisualizarImagem = (imagem: any) => {
    // Simular tamanho do arquivo (em um caso real, você teria essa informação)
    const tamanhoSimulado = Math.floor(Math.random() * 5000000) + 100000; // Entre 100KB e 5MB

    setImagemSelecionada({
      url: imagem.fullsizeUrl || imagem.url, // Usa imagem completa para a modal
      nome: imagem.nome,
      tamanho: formatarTamanhoArquivo(tamanhoSimulado),
      id: imagem.id,
    });
  };

  const handleFecharModal = () => {
    setImagemSelecionada(null);
  };

  const handleRemoverImagemModal = () => {
    if (imagemSelecionada) {
      onRemoverImagem(imagemSelecionada.id);
      setImagemSelecionada(null);
    }
  };

  const podeAdicionarImagem = !loading && imagens.length < maxImagens;

  return (
    <div className="gerenciador-imagens">
      <div className="imagens-container">
        <div className="imagens-actions">
          <button
            type="button"
            className="action-button tirar-foto"
            onClick={() => setMostrarCamera(true)}
            disabled={!podeAdicionarImagem}
          >
            <i className="fas fa-camera" />
            Tirar foto
          </button>
          <label className="action-button enviar-foto">
            <i className="fas fa-upload" />
            Enviar foto
            <input
              ref={inputFileRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={!podeAdicionarImagem}
            />
          </label>
        </div>

        <div className="imagens-grid">
          {imagens.map((imagem) => (
            <div key={imagem.id} className="imagem-item">
              <button
                type="button"
                onClick={() => handleVisualizarImagem(imagem)}
                className="imagem-clicavel"
                style={{ border: 'none', padding: 0, background: 'none' }}
              >
                <img
                  src={imagem.url}
                  alt={imagem.nome}
                />
              </button>
              <button
                type="button"
                className="remover-imagem"
                onClick={() => handleRemoverImagem(imagem.id)}
                disabled={loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <TirarFoto
        visible={mostrarCamera}
        onHide={() => setMostrarCamera(false)}
        onCapture={handleCapturePhoto}
      />

      <ModalVisualizarImagem
        visible={!!imagemSelecionada}
        imagemUrl={imagemSelecionada?.url || null}
        imagemNome={imagemSelecionada?.nome || null}
        imagemTamanho={imagemSelecionada?.tamanho || null}
        onHide={handleFecharModal}
        onRemover={handleRemoverImagemModal}
      />

      <ModalDecisao
        visible={!!imagemParaExcluir}
        title="Excluir imagem?"
        description="Tem certeza que deseja excluir esta imagem?"
        okButtonLabel="Sim, excluir"
        cancelButtonLabel="Não"
        onConfirm={confirmarExclusao}
        onCancel={() => setImagemParaExcluir(null)}
      />
    </div>
  );
}
